import clsx from 'clsx';
import { useModal } from '../hooks/useModal';
import { LazyImage } from './LazyImage';
import { FaChevronLeft, FaChevronRight, FaExpandAlt, FaTimes } from 'react-icons/fa';
import { FaMagnifyingGlassMinus, FaMagnifyingGlassPlus, FaRotateLeft, FaRotateRight } from 'react-icons/fa6';
import { Button } from '../catalyst/button';
import { useEffect, useRef, useState } from 'react';
import { pagePreviewSrc } from '../lib/pagePreviewSrc';
import { TransformComponent, TransformWrapper, type ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';

/**
 * Opens a full-screen, zoomable preview of `pageId`. Pass `pageIds` (the ordered
 * set of "relevant" pages — e.g. a parent's segment children, or a document's
 * pages) to enable prev/next navigation (buttons + ←/→ keys) inside the dialog.
 * Without it the dialog is a single page (nav hidden).
 */
export function FullScreenImagePreviewTrigger({
  pageId,
  pageIds,
  variant,
}: {
  pageId: string;
  pageIds?: string[];
  // DR-2004: which object to show in the fullscreen viewer — defaults to the
  // stored (rotated) page when omitted.
  variant?: 'original' | 'rotated';
}) {
  const modal = useModal();
  const ids = pageIds && pageIds.length > 0 ? pageIds : [pageId];

  return (
    <a
      className="absolute top-0 right-0"
      onClick={(e) => {
        if (!e.metaKey) {
          e.preventDefault();
          // Key the modal by the page the trigger sits on; the dialog flips its
          // displayed page internally, so close must use this stable key.
          modal.create(
            pageId,
            <FullScreenImagePreview
              pageIds={ids}
              initialPageId={pageId}
              variant={variant}
              onClose={() => modal.destroy(pageId)}
            />,
          );
        }
      }}
    >
      <span className="absolute top-2 right-2 text-white group-hover:visible invisible bg-black/30 rounded-md p-2 dark:text-zinc-100 dark:hover:bg-zinc-800 dark:hover:text-zinc-100">
        <FaExpandAlt />
      </span>
    </a>
  );
}

function FullScreenImagePreview({
  pageIds,
  initialPageId,
  variant,
  onClose,
}: {
  pageIds: string[];
  initialPageId: string;
  variant?: 'original' | 'rotated';
  onClose: () => void;
}) {
  const [index, setIndex] = useState(() => Math.max(0, pageIds.indexOf(initialPageId)));
  const pageId = pageIds[index] ?? initialPageId;
  const hasMultiple = pageIds.length > 1;

  // ←/→ flip between the relevant pages (Escape is handled by ModalProvider).
  // Functional updates keep the listener stable — no stale index, no re-subscribe.
  // Registered in the CAPTURE phase with stopPropagation so the arrow keys flip the
  // modal ONLY: other window keydown listeners (e.g. the document-pages grid, which
  // scrolls on the same keys) are bubble-phase and so are pre-empted — otherwise
  // opening the modal from a grid row would flip the image AND scroll the list.
  useEffect(() => {
    if (!hasMultiple) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.stopPropagation();
        setIndex((i) => Math.max(0, i - 1));
      } else if (e.key === 'ArrowRight') {
        e.stopPropagation();
        setIndex((i) => Math.min(pageIds.length - 1, i + 1));
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [hasMultiple, pageIds.length]);

  // z-[70]: must cover the peek dialog (z-[60]) when opened from inside it — drawer(50) < peek(60) < fullscreen/dialog(70).
  return (
    <div role="dialog" aria-modal="true" className="inset-0 z-[70] fixed bg-gray-200 dark:bg-zinc-900">
      {/* key=pageId remounts the viewer on flip so rotation/zoom reset cleanly. */}
      <ImageViewer key={pageId} pageId={pageId} variant={variant} />

      <div className="absolute top-2 right-2 backdrop-blur-sm rounded-md">
        <ControlButton onClick={onClose} icon={<FaTimes />} />
      </div>

      {hasMultiple && (
        <>
          <NavButton side="left" disabled={index <= 0} onClick={() => setIndex((i) => Math.max(0, i - 1))} />
          <NavButton
            side="right"
            disabled={index >= pageIds.length - 1}
            onClick={() => setIndex((i) => Math.min(pageIds.length - 1, i + 1))}
          />
          <div className="absolute top-2 left-1/2 -translate-x-1/2 rounded-md bg-black/40 px-3 py-1 text-sm text-white backdrop-blur-sm">
            {index + 1} / {pageIds.length}
          </div>
        </>
      )}
    </div>
  );
}

// Per-page zoom/pan/rotate viewer. Remounted (via key=pageId) on each flip so its
// rotation/zoom/dimensions reset to defaults for the newly shown page.
function ImageViewer({ pageId, variant }: { pageId: string; variant?: 'original' | 'rotated' }) {
  const [rotation, setRotation] = useState(0);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);

  const width = dimensions?.width ?? 1;
  const height = dimensions?.height ?? 1;
  // Compensate for rotated wide/tall images so a 90° rotation still fits at 1×.
  const aspectScale = rotation % 180 === 0 ? 1 : height / width;

  const rotateBy = (delta: number) => {
    setRotation((r) => r + delta);
    transformRef.current?.resetTransform();
  };

  return (
    <div className="absolute inset-0">
      <TransformWrapper
        ref={transformRef}
        minScale={1}
        maxScale={8}
        initialScale={1}
        wheel={{ step: 0.05, disabled: !dimensions }}
        panning={{ disabled: !dimensions }}
        pinch={{ disabled: !dimensions }}
        doubleClick={{ mode: 'reset', disabled: !dimensions }}
        limitToBounds
      >
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{ width: '100%', height: '100%' }}
        >
          {/* Full-screen viewer: request a high-res render (it's zoomable up to 8×). */}
          <LazyImage
            src={pagePreviewSrc(pageId, { resize: 'x1200', variant })}
            className="w-full h-full bg-gray-200 dark:bg-zinc-900"
            imageClassName={clsx('transition-transform duration-300')}
            imageStyle={{
              transform: `rotate(${rotation}deg) scale(${aspectScale})`,
            }}
            onImageLoaded={(d) => setDimensions(d)}
          />
        </TransformComponent>
      </TransformWrapper>

      <Controls rotateBy={rotateBy} transformRef={transformRef} show={dimensions !== null} />
    </div>
  );
}

function NavButton({ side, disabled, onClick }: { side: 'left' | 'right'; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === 'left' ? 'Previous page' : 'Next page'}
      className={clsx(
        'absolute top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/40 p-3 text-white backdrop-blur-sm transition hover:bg-black/60 disabled:cursor-not-allowed disabled:opacity-20',
        side === 'left' ? 'left-3' : 'right-3',
      )}
    >
      {side === 'left' ? <FaChevronLeft /> : <FaChevronRight />}
    </button>
  );
}

// Module-level cursor tracker so a freshly mounted preview can seed opacity
// from the last known position — without this, controls stay invisible
// (pointer-events: none) until the user moves the mouse, so a cursor that
// happens to already sit over the bottom strip when the modal opens can't
// click the buttons.
let lastCursor: { x: number; y: number } | null = null;
if (typeof window !== 'undefined') {
  window.addEventListener('mousemove', (e) => {
    lastCursor = { x: e.clientX, y: e.clientY };
  });
}

function Controls({
  rotateBy,
  transformRef,
  show,
}: {
  rotateBy: (delta: number) => void;
  transformRef: React.RefObject<ReactZoomPanPinchRef | null>;
  show: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    const PROXIMITY_PX = 200;
    const compute = (x: number, y: number) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const dx = Math.max(rect.left - x, 0, x - rect.right);
      const dy = Math.max(rect.top - y, 0, y - rect.bottom);
      const distance = Math.hypot(dx, dy);
      setOpacity(Math.max(0, Math.min(1, 1 - distance / PROXIMITY_PX)));
    };

    if (lastCursor) compute(lastCursor.x, lastCursor.y);
    else setOpacity(1);

    const handler = (e: MouseEvent) => compute(e.clientX, e.clientY);
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  return (
    <div
      className={clsx(
        'absolute bottom-2 left-2 right-2 flex justify-center pointer-events-none',
        show ? 'visible' : 'invisible',
      )}
    >
      <div
        ref={ref}
        className="bg-zinc-300/10 dark:bg-zinc-800/10 rounded-lg p-2 flex items-center gap-2 backdrop-blur-sm transition-opacity duration-150"
        style={{ opacity, pointerEvents: opacity < 0.1 ? 'none' : 'auto', border: '1px solid rgba(0, 0, 0, 0.05)' }}
      >
        <ControlButton onClick={() => transformRef.current?.zoomOut()} icon={<FaMagnifyingGlassMinus />} />
        <ControlButton onClick={() => transformRef.current?.zoomIn()} icon={<FaMagnifyingGlassPlus />} />
        <ControlButton onClick={() => transformRef.current?.resetTransform()} icon={<span className="text-sm">1×</span>} />
        <div className="w-px h-6 bg-zinc-500/30 mx-1" aria-hidden />
        <ControlButton onClick={() => rotateBy(-90)} icon={<FaRotateLeft />} />
        <ControlButton onClick={() => rotateBy(90)} icon={<FaRotateRight />} />
      </div>
    </div>
  );
}

function ControlButton({ onClick, icon }: { onClick: () => void; icon: React.ReactNode }) {
  return (
    <Button plain className="lg:text-2xl lg:p-3 lg:px-6" onClick={onClick}>
      {icon}
    </Button>
  );
}
